"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FeedEvents, trackEvent } from "@/analytics";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaCarouselType } from "embla-carousel";
import ClassNames from "embla-carousel-class-names";
import CarouselLeftButton from "@/components/common/carousels/CarouselLeftButton";
import CarouselRightButton from "@/components/common/carousels/CarouselRightButton";
import CarouselLeftGradient from "@/components/common/carousels/CarouselLeftGradient";
import CarouselRightGradient from "@/components/common/carousels/CarouselRightGradient";
import { useImageCenteredArrowOffset } from "@/components/common/carousels/useImageCenteredArrowOffset";

export interface SlideData {
  id: string;
  Component: React.ReactNode;
}

interface Props {
  title: string;
  slides: SlideData[];
  location: string;
}

const FeedCarousel = ({ title, slides, location }: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: false, dragFree: true },
    [ClassNames()]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);

  // Center the arrows on each slide's thumbnail rather than the full card
  // (which also includes the title/runtime below the thumbnail).
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const arrowTop = useImageCenteredArrowOffset(containerRef, trackRef, [
    slides,
  ]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(
    (embla: EmblaCarouselType) => {
      const newIndex = embla.selectedScrollSnap();

      // Track analytics based on direction - captures ALL scroll methods
      if (newIndex > selectedIndex) {
        trackEvent(FeedEvents.CLICKED_NEXT_CONTINUE_WATCHING_FEED_CAROUSEL, {
          location,
        });
      } else if (newIndex < selectedIndex) {
        trackEvent(
          FeedEvents.CLICKED_PREVIOUS_CONTINUE_WATCHING_FEED_CAROUSEL,
          {
            location,
          }
        );
      }

      setSelectedIndex(newIndex);
      setCanScrollPrev(embla.canScrollPrev());
      setCanScrollNext(embla.canScrollNext());
    },
    [location, selectedIndex]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi); // run once on mount
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  if (!slides || slides.length === 0) return null;

  return (
    <div className="w-full pb-4 md:pb-6 md:pt-2">
      <h2 className="font-sans text-slate-gray-900 normal-case font-medium text-[20px] pr-2 truncate mb-3 md:mb-4 px-4 md:px-0">
        {title}
      </h2>
      <div className="relative overflow-hidden" ref={containerRef}>
        <CarouselLeftGradient isVisible={canScrollPrev} />
        <CarouselLeftButton
          onClick={onPrevButtonClick}
          isDisabled={!canScrollPrev}
          style={arrowTop !== null ? { top: arrowTop } : undefined}
        />

        <CarouselRightGradient isVisible={canScrollNext} />
        <CarouselRightButton
          onClick={onNextButtonClick}
          isDisabled={!canScrollNext}
          style={arrowTop !== null ? { top: arrowTop } : undefined}
        />

        <div
          className="overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          ref={emblaRef}
        >
          <div
            className="flex flex-row mr-4 touch-pan-y backface-hidden gap-x-4 px-4 md:px-0"
            ref={trackRef}
          >
            {slides.map(({ Component, id }) => (
              <React.Fragment key={id}>{Component}</React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedCarousel;
